/**
 * Bill composer page — shared between `/procurement/bills/new` and
 * `/procurement/bills/$billId/edit`.
 *
 * Mode is set by the optional `loadBillId` prop (mirror of the invoice
 * composer's `loadDraftId`):
 *   - `undefined` → new-bill mode. Empty form. Save = `bills.create`.
 *   - `<uuid>`    → edit mode. Hydrate from `bills.get`. Save = `bills.update`.
 *
 * Edit-mode constraints (from the wire contract):
 *   - Only `draft` and `review` bills are editable — a locked banner shows
 *     for anything else and the save buttons are disabled.
 *   - Vendor is locked (contract doesn't accept `vendorId` on update).
 *     The vendor picker still renders but `onVendorChange` is a no-op.
 *   - Source mode is locked (would change provenance). `onModeChange` is a no-op.
 *   - Bill lines are persisted as purchase-side line snapshots. Older rows
 *     without lines still hydrate from totals as a fallback.
 *
 * @see prototype/purchases/bill-new.html — V1.1 baseline (S15/S16)
 * @see flowtrail/erp ▸ docs/internal/product/lucid-v1-demo-scope.md §3.3 — vendor bill OCR upload + review
 * @see AGENTS.md §4 — Forms use react-hook-form + Zod, schemas shared with tRPC
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useForm, useWatch, type UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ArrowLeft,
  Check,
  FileText,
  Loader2,
  Lock,
  Mail,
  Sparkles,
  Upload,
  X,
} from "lucide-react";
import { Alert, Button, ConfirmDialog, Form, toast } from "@flint/ui";
import { useQuery } from "@tanstack/react-query";
import { trpc } from "../../../lib/trpc.js";
import { meQueryOptions } from "../../../lib/session.js";
import { computeTotals, hasValidBillLine } from "./bill-math.js";
import { BillIdentitySection } from "./bill-identity-section.js";
import { BillPreview } from "./bill-preview.js";
import { LineItemsSection } from "./line-items-section.js";
import { MatchSection } from "./match-section.js";
import { NewBillFooter } from "./new-bill-footer.js";
import { SourceCard } from "./source-card.js";
import { TaxApprovalSection } from "./tax-approval-section.js";
import { VendorSection } from "./vendor-section.js";
import {
  EMPTY_BILL_FORM,
  EMPTY_LINE,
  billFormSchema,
  type BillFormValues,
  type BillLineItem,
  type BillSourceMode,
} from "./lib/form-schema.js";
import type { BillListRow } from "../list/types.js";
import type { VendorBillSummary, VendorOption } from "./types.js";

/**
 * Snapshot of a successfully uploaded scratch object — the file picked,
 * plus the server-minted object key + content metadata we need at save
 * time to promote it into a permanent attachment row.
 */
type ScratchUpload = {
  file: File;
  objectKey: string;
  contentType: "application/pdf" | "image/jpeg" | "image/png" | "image/webp";
  sizeBytes: number;
};

/**
 * Snapshot of the most recent `bills.extract` response. We mirror the
 * contract shape inline (rather than importing the inferred tRPC type)
 * so a stash → save cycle survives unmount/remount and the type stays
 * legible right where it's used.
 */
type ExtractResultSnapshot = {
  fields: {
    vendor: {
      name: string;
      gstin: string | null;
      pan: string | null;
      address: string | null;
    };
    vendorDocNumber: string;
    receivedOn: string;
    dueOn: string | null;
    lineItems: Array<{
      description: string;
      hsn: string;
      qty: string;
      unit: string;
      rate: string;
      taxRate: string;
      discountRate: string;
    }>;
    reportedSubtotal: string | null;
    reportedTax: string | null;
    reportedTotal: string | null;
    notes: string | null;
  };
  fieldConfidence: Record<string, number>;
  overallConfidence: number;
  model: string;
  promptVersion: string;
  costPaise: string;
};

export interface BillComposerPageProps {
  /** When set, the composer hydrates from `bills.get({ id: loadBillId })`
   *  and saves via `bills.update` instead of `bills.create`. */
  loadBillId?: string;
  /**
   * Initial source mode for new-bill mode (ignored in edit mode where
   * provenance is locked). Defaults to `'upload'` — the standard OCR flow.
   */
  initialSourceMode?: BillSourceMode;
}

export function BillComposerPage({ loadBillId, initialSourceMode }: BillComposerPageProps = {}) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const vendors = trpc.vendors.list.useQuery({ limit: 100 });
  // Company state drives intra-vs-inter-state tax-mode inference. Was
  // hardcoded to "Maharashtra" until this fix — that misclassified every
  // tenant whose company isn't in Maharashtra (e.g. Loomwell in Karnataka:
  // a Karnataka vendor would have been tagged IGST instead of CGST+SGST).
  const me = useQuery(meQueryOptions);
  const companyState = me.data?.company?.state ?? "";
  const isEditMode = !!loadBillId;

  // Edit-mode: load the existing bill row. The query is disabled on the
  // new path so it doesn't pay a round-trip.
  const loadedBill = trpc.bills.get.useQuery({ id: loadBillId ?? "" }, { enabled: isEditMode });

  /**
   * Bill id either inherited from the route (edit) or assigned by the
   * server after the first save-draft (new). Once set, the attachments
   * card activates (it needs an FK to bind to).
   */
  const [savedBillId, setSavedBillId] = useState<string | null>(loadBillId ?? null);
  /**
   * Legacy "stage file in parent → flush after savedBillId" path — kept
   * for EDIT mode only. New-bill mode uses the scratch upload + extract
   * path below (`scratchUpload`).
   */
  const [pendingUpload, setPendingUpload] = useState<File | null>(null);
  const [pendingUploadState, setPendingUploadState] = useState<
    "idle" | "requesting" | "uploading" | "confirming" | "done" | "error"
  >("idle");
  const [pendingUploadError, setPendingUploadError] = useState<string | null>(null);

  /**
   * Pre-bill scratch upload — new-bill mode only. The file PUTs to a
   * tenant-scoped scratch key immediately on pick; the operator then
   * (optionally) clicks "Extract with AI" to pre-fill the form. On save,
   * `bills.create` promotes the scratch object into a permanent
   * attachment in the same TX (via `extractionSourceObjectKey`).
   */
  const [scratchUpload, setScratchUpload] = useState<ScratchUpload | null>(null);
  const [scratchUploadState, setScratchUploadState] = useState<
    "idle" | "uploading" | "uploaded" | "error"
  >("idle");
  const [scratchUploadError, setScratchUploadError] = useState<string | null>(null);

  /**
   * Result of the most recent `bills.extract` call. Stashed so the save
   * handler can forward `aiExtraction.*` + `overallConfidence` onto the
   * created bill row (audit trail).
   */
  const [extractResult, setExtractResult] = useState<ExtractResultSnapshot | null>(null);
  const [extractState, setExtractState] = useState<"idle" | "extracting" | "done" | "error">(
    "idle",
  );
  const [extractError, setExtractError] = useState<string | null>(null);
  /**
   * Name the model extracted that we couldn't match to an existing vendor.
   * Surfaced as a hint above the vendor picker so the operator knows what
   * to look for / who to create.
   */
  const [unmatchedVendorName, setUnmatchedVendorName] = useState<string | null>(null);
  /** Re-extract confirm dialog open state. */
  const [reExtractConfirmOpen, setReExtractConfirmOpen] = useState(false);

  const create = trpc.bills.create.useMutation({
    onSuccess: async () => {
      await utils.bills.list.invalidate();
    },
  });

  const updateBill = trpc.bills.update.useMutation({
    onSuccess: async () => {
      if (loadBillId) {
        await Promise.all([
          utils.bills.get.invalidate({ id: loadBillId }),
          utils.bills.list.invalidate(),
        ]);
      }
    },
  });

  const transition = trpc.bills.transitionStatus.useMutation({
    onSuccess: async () => {
      if (loadBillId) {
        await Promise.all([
          utils.bills.get.invalidate({ id: loadBillId }),
          utils.bills.list.invalidate(),
        ]);
      }
    },
  });

  // Attachment-upload hooks reused by the Upload-mode flush effect below
  // (EDIT mode only — new mode uses the scratch + extract flow).
  const requestAttachmentUpload = trpc.bills.attachments.requestUpload.useMutation();
  const confirmAttachment = trpc.bills.attachments.confirm.useMutation();

  // New-bill scratch upload + AI extract mutations.
  const requestScratchUpload = trpc.bills.requestScratchUpload.useMutation();
  const extractBill = trpc.bills.extract.useMutation();

  /**
   * Once a draft exists AND there's a staged file in pendingUpload, push it
   * through the standard 3-step pipeline. Runs at most once per file (state
   * machine guards re-entry); errors surface via the dropzone strip below.
   */
  useEffect(() => {
    if (!savedBillId || !pendingUpload) return;
    if (pendingUploadState !== "idle") return;

    let cancelled = false;
    const file = pendingUpload;
    (async () => {
      try {
        setPendingUploadState("requesting");
        const req = await requestAttachmentUpload.mutateAsync({
          billId: savedBillId,
          originalName: file.name,
          contentType: file.type as "application/pdf" | "image/jpeg" | "image/png" | "image/webp",
          sizeBytes: file.size,
        });
        if (cancelled) return;

        setPendingUploadState("uploading");
        const putRes = await fetch(req.uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": file.type },
          body: file,
        });
        if (!putRes.ok) {
          throw new Error(`Upload failed: ${putRes.status} ${putRes.statusText || ""}`);
        }
        if (cancelled) return;

        setPendingUploadState("confirming");
        await confirmAttachment.mutateAsync({
          billId: savedBillId,
          objectKey: req.objectKey,
          originalName: file.name,
          contentType: file.type as "application/pdf" | "image/jpeg" | "image/png" | "image/webp",
          sizeBytes: file.size,
        });
        if (cancelled) return;

        // Refresh the attachments list shown below the form.
        await utils.bills.attachments.list.invalidate({ billId: savedBillId });
        setPendingUploadState("done");
        toast.success("File attached", {
          description: `"${file.name}" is now on this bill.`,
        });
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : "Upload failed";
        setPendingUploadError(message);
        setPendingUploadState("error");
        toast.error("Couldn't attach the file", { description: message });
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutation refs are stable
  }, [savedBillId, pendingUpload]);

  // ── New-bill scratch upload + extract handlers ────────────────────
  //
  // These run only in new-bill mode (`!isEditMode`). The dropzone props
  // below are wired to call them when the operator picks a file or hits
  // the Extract button. Edit mode keeps the legacy `pendingUpload` flow.

  /** Three-step scratch upload: requestScratchUpload → S3 PUT → store result. */
  const startScratchUpload = async (file: File) => {
    setScratchUploadError(null);
    setScratchUpload({
      file,
      objectKey: "",
      contentType: file.type as ScratchUpload["contentType"],
      sizeBytes: file.size,
    });
    setScratchUploadState("uploading");
    try {
      const req = await requestScratchUpload.mutateAsync({
        originalName: file.name,
        contentType: file.type as ScratchUpload["contentType"],
        sizeBytes: file.size,
      });
      const putResponse = await fetch(req.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!putResponse.ok) {
        throw new Error(`Upload failed: ${putResponse.status} ${putResponse.statusText || ""}`);
      }
      setScratchUpload({
        file,
        objectKey: req.objectKey,
        contentType: file.type as ScratchUpload["contentType"],
        sizeBytes: file.size,
      });
      setScratchUploadState("uploaded");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Upload failed.";
      setScratchUploadError(message);
      setScratchUploadState("error");
      toast.error("Couldn't upload the file", { description: message });
    }
  };

  /** Clear the scratch upload + any associated extract state. */
  const clearScratchUpload = () => {
    setScratchUpload(null);
    setScratchUploadState("idle");
    setScratchUploadError(null);
    setExtractResult(null);
    setExtractState("idle");
    setExtractError(null);
    setUnmatchedVendorName(null);
  };

  /**
   * Run `bills.extract` against the uploaded scratch object. On success,
   * map the response into the form via `applyExtractToForm` (vendor
   * lookup, line items, notes, etc.). The mapper also surfaces an
   * unmatched-vendor hint when neither GSTIN nor name resolves.
   */
  const runExtract = async () => {
    if (!scratchUpload || scratchUploadState !== "uploaded") return;
    setExtractError(null);
    setExtractState("extracting");
    try {
      const result = await extractBill.mutateAsync({
        sourceObjectKey: scratchUpload.objectKey,
        contentType: scratchUpload.contentType,
      });
      const snapshot = result as ExtractResultSnapshot;
      setExtractResult(snapshot);
      setExtractState("done");
      const unmatched = applyExtractToForm(snapshot, vendors.data?.items ?? [], form);
      setUnmatchedVendorName(unmatched);
      toast.success("Extracted from PDF", {
        description: `Filled the form at ${Math.round(snapshot.overallConfidence * 100)}% overall confidence — review every line before saving.`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Extraction failed.";
      setExtractError(message);
      setExtractState("error");
      toast.error("Couldn't extract", { description: message });
    }
  };

  const vendorOptions = useMemo<VendorOption[]>(
    () =>
      (vendors.data?.items ?? []).map((vendor) => ({
        id: vendor.id,
        name: vendor.name,
        contactName: vendor.contactName,
        gstin: vendor.gstin,
        phone: vendor.phone,
        city: vendor.city,
        state: vendor.state,
        paymentTerms:
          "paymentTerms" in vendor
            ? (vendor as { paymentTerms: string | null }).paymentTerms
            : null,
        tdsSection:
          "tdsSection" in vendor ? (vendor as { tdsSection: string | null }).tdsSection : null,
      })),
    [vendors.data?.items],
  );

  const form = useForm<BillFormValues>({
    resolver: zodResolver(billFormSchema),
    defaultValues: {
      ...EMPTY_BILL_FORM,
      // Honour an explicit initial source on new-bill mode. Edit mode
      // hydrates from the bill row in the effect below, so this is ignored.
      ...(initialSourceMode ? { sourceMode: initialSourceMode } : {}),
    },
    mode: "onBlur",
  });

  // Hydrate the form once when the bill row lands. Guarded by a ref so a
  // second render doesn't clobber operator edits in progress.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    if (!isEditMode) return;
    if (!loadedBill.data) return;
    hydratedRef.current = true;
    const bill = loadedBill.data;
    form.reset({
      ...EMPTY_BILL_FORM,
      sourceMode: sourceToMode(bill.source),
      extractionState: "idle",
      vendorId: bill.vendorId,
      vendorDocNumber: bill.vendorDocNumber ?? "",
      receivedOn: bill.receivedOn,
      dueOn: bill.dueOn ?? "",
      placeOfSupply: bill.vendorState ?? "",
      grnNumber: bill.threeWayMatch === "matched" ? "matched-on-capture" : "",
      vendorNote: bill.vendorNote ?? "",
      internalNote: bill.internalNote ?? "",
      approver: bill.approver ?? "",
      lineItems:
        bill.lineItems.length > 0
          ? bill.lineItems.map(lineFromPersisted)
          : [synthesiseLineFromTotals(bill.subtotalPaise, bill.taxPaise)],
    });
  }, [isEditMode, loadedBill.data, form]);

  const values = useWatch({ control: form.control }) as BillFormValues;
  const selectedVendorDetail = trpc.vendors.get.useQuery(
    { id: values.vendorId || "" },
    { enabled: Boolean(values.vendorId) },
  );
  const selectedVendorBills = trpc.bills.list.useQuery(
    { limit: 100, ...(values.vendorId ? { vendorId: values.vendorId } : {}) },
    { enabled: Boolean(values.vendorId) },
  );
  const totals = useMemo(
    () => computeTotals(values.lineItems ?? [], values.taxMode),
    [values.lineItems, values.taxMode],
  );
  const errors = form.formState.errors;
  const vendorChoices = useMemo(() => {
    const detail = selectedVendorDetail.data;
    if (!detail) return vendorOptions;
    return vendorOptions.map((vendor) =>
      vendor.id === detail.id
        ? {
            ...vendor,
            contactName: detail.contactName,
            gstin: detail.gstin,
            phone: detail.phone,
            email: detail.email,
            addressLine1: detail.addressLine1,
            addressLine2: detail.addressLine2,
            pincode: detail.pincode,
            city: detail.city,
            state: detail.state,
            paymentTerms: detail.paymentTerms,
            tdsSection: detail.tdsSection,
          }
        : vendor,
    );
  }, [selectedVendorDetail.data, vendorOptions]);
  const selectedVendor = vendorChoices.find((vendor) => vendor.id === values.vendorId) ?? null;
  const selectedVendorSummary = useMemo(
    () => buildVendorSummary((selectedVendorBills.data?.items ?? []) as BillListRow[]),
    [selectedVendorBills.data?.items],
  );

  /** Field-level patcher shared with the (still-prop-shaped) subcomponents. */
  const patch = <K extends keyof BillFormValues>(field: K, value: BillFormValues[K]) => {
    form.setValue(field, value as never, { shouldDirty: true, shouldTouch: true });
    form.clearErrors(field);
  };

  /**
   * Auto-fill the place-of-supply / payment-terms / TDS hints when a vendor
   * is picked. In edit mode the vendor is locked (the contract doesn't
   * accept `vendorId` on update), so the handler is a no-op there.
   */
  const onVendorChange = (vendorId: string) => {
    if (isEditMode) return;
    const vendor = vendorOptions.find((v) => v.id === vendorId);
    form.setValue("vendorId", vendorId, { shouldDirty: true, shouldTouch: true });
    if (vendor) {
      if (vendor.paymentTerms) form.setValue("paymentTerms", vendor.paymentTerms);
      if (vendor.state) {
        form.setValue("placeOfSupply", vendor.state);
        // Intra-state when the vendor sits in the same state as our company;
        // otherwise inter-state IGST. (Was hardcoded to "Maharashtra" — the
        // same stale-hardcode class as the companyState fix above; it
        // mis-tagged every non-MH vendor as IGST.)
        form.setValue("taxMode", vendor.state === companyState ? "intra" : "inter");
      }
      form.setValue("category", defaultVendorCategory(vendor));
      if (vendor.tdsSection) {
        form.setValue("tdsSection", vendor.tdsSection);
        form.setValue("tdsRate", defaultTdsRate(vendor.tdsSection));
      }
    }
    form.clearErrors("vendorId");
  };

  /** Source mode is locked on edit — provenance shouldn't change. */
  const onSourceModeChange = (mode: BillSourceMode) => {
    if (isEditMode) return;
    patch("sourceMode", mode);
  };

  const submit = form.handleSubmit(async (parsedValues) => {
    const linesWithContent = parsedValues.lineItems.filter(hasValidBillLine);
    if (linesWithContent.length === 0) {
      form.setError("lineItems", {
        type: "manual",
        message: "Add at least one line with description, quantity, and rate.",
      });
      return;
    }

    const finalTotals = computeTotals(parsedValues.lineItems, parsedValues.taxMode);
    if (finalTotals.totalPaise <= 0n) {
      form.setError("lineItems", {
        type: "manual",
        message: "Bill total must be greater than zero.",
      });
      return;
    }

    try {
      if (isEditMode && loadBillId) {
        // Edit-mode save — call bills.update with the header fields.
        // Vendor + source aren't accepted on the wire; we re-send the
        // captured source so the row's provenance doesn't shift.
        await updateBill.mutateAsync({
          id: loadBillId,
          vendorDocNumber: parsedValues.vendorDocNumber.trim(),
          receivedOn: parsedValues.receivedOn,
          dueOn: parsedValues.dueOn || null,
          subtotalPaise: finalTotals.subtotalPaise.toString(),
          taxPaise: finalTotals.taxPaise.toString(),
          totalPaise: finalTotals.totalPaise.toString(),
          threeWayMatch: parsedValues.grnNumber.trim() ? "matched" : "na",
          source: loadedBill.data?.source ?? sourceToWire(parsedValues.sourceMode),
          vendorNote: parsedValues.vendorNote || null,
          internalNote: parsedValues.internalNote || null,
          approver: parsedValues.approver || null,
          lineItems: toPersistedLineInput(parsedValues.lineItems),
        });
        toast.success("Bill updated", {
          description: `${parsedValues.vendorDocNumber.trim() || "Draft"} saved. Returning to the bill detail.`,
        });
        void navigate({ to: "/procurement/bills/$billId", params: { billId: loadBillId } });
        return;
      }

      // New-bill path. If we've already saved once on this page, "Save
      // draft" is a no-op to avoid duplicates.
      if (savedBillId) return;

      const result = await create.mutateAsync({
        vendorId: parsedValues.vendorId,
        vendorDocNumber: parsedValues.vendorDocNumber.trim(),
        receivedOn: parsedValues.receivedOn,
        dueOn: parsedValues.dueOn || null,
        subtotalPaise: finalTotals.subtotalPaise.toString(),
        taxPaise: finalTotals.taxPaise.toString(),
        totalPaise: finalTotals.totalPaise.toString(),
        status: "draft",
        threeWayMatch: parsedValues.grnNumber.trim() ? "matched" : "na",
        source: sourceToWire(parsedValues.sourceMode),
        // AI extract metadata when the operator ran `bills.extract` on
        // the uploaded scratch object. Null when they typed manually or
        // uploaded without extracting.
        aiExtraction: buildAiExtractionPayload(extractResult),
        overallConfidence: buildOverallConfidencePayload(extractResult),
        vendorNote: parsedValues.vendorNote || null,
        internalNote: parsedValues.internalNote || null,
        approver: parsedValues.approver || null,
        lineItems: toPersistedLineInput(parsedValues.lineItems),
        // Promote the scratch upload (if any) into a permanent
        // attachment row in the same TX. Server-side asserted to live
        // under the tenant's scratch prefix.
        ...buildScratchPromotePayload(scratchUpload, scratchUploadState),
      });

      toast.success("Saved as draft", {
        description: "Keep editing, attach files, or submit for approval when ready.",
      });

      // Keep the user on the page so the attachments section unlocks. The
      // submit-for-approval path still navigates after the bill is created.
      setSavedBillId(result.id);
    } catch (err) {
      toast.error("Couldn't save bill", {
        description: err instanceof Error ? err.message : "Try again in a moment.",
      });
    }
  });

  /** Submit-for-approval variant. */
  const submitForApproval = form.handleSubmit(async (parsedValues) => {
    const linesWithContent = parsedValues.lineItems.filter(hasValidBillLine);
    if (linesWithContent.length === 0) {
      form.setError("lineItems", {
        type: "manual",
        message: "Add at least one line with description, quantity, and rate.",
      });
      return;
    }
    const finalTotals = computeTotals(parsedValues.lineItems, parsedValues.taxMode);

    try {
      if (isEditMode && loadBillId) {
        // Edit-mode submit: save header fields first, then flip status.
        await updateBill.mutateAsync({
          id: loadBillId,
          vendorDocNumber: parsedValues.vendorDocNumber.trim(),
          receivedOn: parsedValues.receivedOn,
          dueOn: parsedValues.dueOn || null,
          subtotalPaise: finalTotals.subtotalPaise.toString(),
          taxPaise: finalTotals.taxPaise.toString(),
          totalPaise: finalTotals.totalPaise.toString(),
          threeWayMatch: parsedValues.grnNumber.trim() ? "matched" : "na",
          source: loadedBill.data?.source ?? sourceToWire(parsedValues.sourceMode),
          vendorNote: parsedValues.vendorNote || null,
          internalNote: parsedValues.internalNote || null,
          approver: parsedValues.approver || null,
          lineItems: toPersistedLineInput(parsedValues.lineItems),
        });
        await transition.mutateAsync({ id: loadBillId, status: "approval_pending" });
        toast.success("Submitted for approval", {
          description: "Founder gets a notification to approve and release payment.",
        });
        void navigate({ to: "/procurement/bills/$billId", params: { billId: loadBillId } });
        return;
      }

      // New-bill path: once the draft was already saved (sticky savedBillId),
      // we just navigate. Status-flip on a freshly-created draft lands in V1.x.
      if (savedBillId) {
        void navigate({ to: "/procurement/bills" });
        return;
      }

      await create.mutateAsync({
        vendorId: parsedValues.vendorId,
        vendorDocNumber: parsedValues.vendorDocNumber.trim(),
        receivedOn: parsedValues.receivedOn,
        dueOn: parsedValues.dueOn || null,
        subtotalPaise: finalTotals.subtotalPaise.toString(),
        taxPaise: finalTotals.taxPaise.toString(),
        totalPaise: finalTotals.totalPaise.toString(),
        status: "approval_pending",
        threeWayMatch: parsedValues.grnNumber.trim() ? "matched" : "na",
        source: sourceToWire(parsedValues.sourceMode),
        aiExtraction: buildAiExtractionPayload(extractResult),
        overallConfidence: buildOverallConfidencePayload(extractResult),
        vendorNote: parsedValues.vendorNote || null,
        internalNote: parsedValues.internalNote || null,
        approver: parsedValues.approver || null,
        lineItems: toPersistedLineInput(parsedValues.lineItems),
        ...buildScratchPromotePayload(scratchUpload, scratchUploadState),
      });
      toast.success("Submitted for approval", {
        description: "Founder gets a notification to approve and release payment.",
      });
      void navigate({ to: "/procurement/bills" });
    } catch (err) {
      toast.error("Couldn't submit bill", {
        description: err instanceof Error ? err.message : "Try again in a moment.",
      });
    }
  });

  // Lifecycle gates for edit mode.
  const editableStatuses: ReadonlyArray<string> = ["draft", "review"];
  const isLoadedAndUneditable =
    isEditMode &&
    loadedBill.data !== undefined &&
    !editableStatuses.includes(loadedBill.data.status);

  const ready =
    Object.keys(errors).length === 0 &&
    !!values.vendorId &&
    !!values.vendorDocNumber.trim() &&
    !!values.receivedOn &&
    totals.totalPaise > 0n &&
    !isLoadedAndUneditable;

  // Loading / error states for edit mode (the new-bill path renders
  // immediately because there's no bill to load).
  if (isEditMode && loadedBill.isLoading) {
    return <p className="p-6 text-sm text-muted">Loading bill…</p>;
  }
  if (isEditMode && loadedBill.error) {
    return <Alert intent="bad" title="Couldn't load bill" description={loadedBill.error.message} />;
  }

  const headerLabel = isEditMode ? "Edit bill" : "New bill";
  const headerSub = isEditMode
    ? `Editing ${loadedBill.data?.vendorDocNumber ?? "bill"} — only header fields update; vendor and source are locked.`
    : savedBillId
      ? "Draft saved. Add attachments below or submit for approval when ready."
      : "Record a vendor invoice — upload the PDF and we'll extract the fields, or enter manually.";

  return (
    <Form {...form}>
      <form onSubmit={submit} className="min-h-full bg-bg">
        {/* Composer head — sticky band, matches prototype/purchases/bill-new.html .composer-head */}
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3.5 border-b border-line bg-paper px-6">
          <Link
            to={isEditMode && loadBillId ? "/procurement/bills/$billId" : "/procurement/bills"}
            {...(isEditMode && loadBillId ? { params: { billId: loadBillId } } : {})}
            aria-label={isEditMode ? "Back to bill" : "Back to bills"}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-sm border border-line text-ink-2 transition hover:bg-bg"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="flex items-center gap-2 text-base font-bold leading-tight tracking-tight text-ink">
              {headerLabel}
              {isEditMode ? (
                <span className="rounded-full bg-raised px-2 py-0.5 font-mono text-xs font-medium text-muted">
                  {loadedBill.data?.vendorDocNumber ?? "bill"}
                </span>
              ) : (
                <span className="rounded-full bg-raised px-2 py-0.5 font-mono text-xs font-medium text-muted">
                  Draft
                </span>
              )}
              {!isEditMode && savedBillId ? (
                <span className="rounded-full bg-ok-50 px-2 py-0.5 text-xs font-medium text-ok">
                  Saved · attachments unlocked
                </span>
              ) : null}
              {isLoadedAndUneditable ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-warn-50 px-2 py-0.5 text-xs font-medium text-warn">
                  <Lock className="h-3 w-3" />
                  Locked · {(loadedBill.data?.status ?? "").replace("_", " ")}
                </span>
              ) : null}
            </h1>
            <p className="mt-0.5 text-xs text-muted">{headerSub}</p>
          </div>
        </header>

        {/* Composer body — 1.5fr / 1fr per prototype .composer-body */}
        <main className="mx-auto grid max-w-[1400px] gap-5 px-6 pb-24 pt-5 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
          <div className="flex min-w-0 flex-col gap-4">
            {vendors.error ? (
              <Alert
                intent="bad"
                title="Couldn't load vendors"
                description={vendors.error.message}
              />
            ) : null}
            {create.error ? (
              <Alert intent="bad" title="Couldn't save bill" description={create.error.message} />
            ) : null}
            {updateBill.error ? (
              <Alert
                intent="bad"
                title="Couldn't update bill"
                description={updateBill.error.message}
              />
            ) : null}
            {transition.error ? (
              <Alert
                intent="bad"
                title="Couldn't change status"
                description={transition.error.message}
              />
            ) : null}
            {isLoadedAndUneditable ? (
              <Alert
                intent="warn"
                title="Bill is locked"
                description="Only draft and review bills can be edited. Use Send back from the bill view to return it to review."
              />
            ) : null}
            {isEditMode ? (
              loadedBill.data?.lineItems.length === 0 ? (
                <Alert
                  intent="info"
                  title="Line items reconstructed from totals"
                  description="This older bill was saved before line retention. The row below represents captured subtotal + tax; saving it now will persist editable lines."
                />
              ) : null
            ) : null}

            <SourceCard mode={values.sourceMode ?? "upload"} onModeChange={onSourceModeChange}>
              <SourceModeBody
                mode={values.sourceMode ?? "upload"}
                isEditMode={isEditMode}
                scratchUpload={scratchUpload}
                scratchUploadState={scratchUploadState}
                scratchUploadError={scratchUploadError}
                extractState={extractState}
                extractError={extractError}
                extractResult={extractResult}
                onScratchPick={(file) => void startScratchUpload(file)}
                onScratchClear={clearScratchUpload}
                onExtract={() => {
                  // Re-extract confirm: if extraction has already run
                  // OR the form has been edited since the page loaded,
                  // ask first — extract REPLACES form fields.
                  if (extractState === "done" || form.formState.isDirty) {
                    setReExtractConfirmOpen(true);
                    return;
                  }
                  void runExtract();
                }}
                pendingUpload={pendingUpload}
                pendingUploadState={pendingUploadState}
                pendingUploadError={pendingUploadError}
                onFilePicked={(file) => {
                  setPendingUpload(file);
                  setPendingUploadError(null);
                  setPendingUploadState("idle");
                }}
                onClearFile={() => {
                  // Can clear while staged (no bill yet) — after upload starts
                  // the dropzone hides the clear button.
                  if (pendingUploadState === "idle" || pendingUploadState === "error") {
                    setPendingUpload(null);
                    setPendingUploadError(null);
                    setPendingUploadState("idle");
                  }
                }}
              />
            </SourceCard>

            {unmatchedVendorName ? (
              <Alert
                intent="warn"
                title="Flint couldn't match the vendor"
                description={`Extracted "${unmatchedVendorName}" — pick the closest vendor below, or open Vendors → New to create one. Once created, come back and the next extract will auto-match.`}
              />
            ) : null}

            <VendorSection
              vendors={vendorChoices}
              vendorId={values.vendorId}
              placeOfSupply={values.placeOfSupply ?? ""}
              category={values.category ?? ""}
              currency={values.currency ?? "INR"}
              summary={selectedVendorSummary}
              companyState={companyState}
              onVendorChange={onVendorChange}
              onFieldChange={(field, value) => {
                patch(field, value);
                if (field === "placeOfSupply") {
                  // Intra-state when the place-of-supply matches the
                  // company state; otherwise inter-state IGST.
                  patch("taxMode", value === companyState ? "intra" : "inter");
                }
              }}
              error={errors.vendorId?.message}
            />

            <BillIdentitySection
              values={values}
              errors={{
                vendorDocNumber: errors.vendorDocNumber?.message,
                receivedOn: errors.receivedOn?.message,
                dueOn: errors.dueOn?.message,
              }}
              onChange={patch}
            />
            <MatchSection values={values} onChange={patch} />
            <LineItemsSection
              rows={values.lineItems ?? []}
              error={errors.lineItems?.message ?? errors.lineItems?.root?.message}
              onRowsChange={(lineItems) => patch("lineItems", lineItems)}
            />
            <TaxApprovalSection
              values={values}
              totals={totals}
              onChange={patch}
              savedBillId={savedBillId}
              companyState={companyState}
            />
          </div>

          <BillPreview values={values} vendor={selectedVendor} totals={totals} />
        </main>

        <NewBillFooter
          values={values}
          vendor={selectedVendor}
          totals={totals}
          ready={ready}
          pending={create.isPending || updateBill.isPending || transition.isPending}
          onCancel={() =>
            isEditMode && loadBillId
              ? void navigate({ to: "/procurement/bills/$billId", params: { billId: loadBillId } })
              : void navigate({ to: "/procurement/bills" })
          }
          onSaveDraft={() => void submit()}
          onSubmit={() => void submitForApproval()}
        />

        <ConfirmDialog
          open={reExtractConfirmOpen}
          onOpenChange={setReExtractConfirmOpen}
          title="Re-extract from this file?"
          description="Running Extract again replaces every field on the form below — including anything you've edited since the last extract. The uploaded file stays in place."
          intent="default"
          confirmLabel="Re-extract"
          onConfirm={() => {
            setReExtractConfirmOpen(false);
            void runExtract();
          }}
        />
      </form>
    </Form>
  );
}

function sourceToWire(mode: BillSourceMode): "photo" | "email_pdf" | "manual" {
  if (mode === "upload") return "photo";
  if (mode === "email") return "email_pdf";
  return "manual";
}

function buildVendorSummary(rows: BillListRow[]): VendorBillSummary | null {
  if (rows.length === 0) {
    return {
      billCount: 0,
      paidBillCount: 0,
      firstBillOn: null,
      lastBillNumber: null,
      lastBillOn: null,
      lastBillTotalPaise: null,
    };
  }

  const sortedByReceived = [...rows].sort(
    (a, b) => a.receivedOn.localeCompare(b.receivedOn) || a.createdAt.localeCompare(b.createdAt),
  );
  const first = sortedByReceived[0] ?? null;
  const last = sortedByReceived[sortedByReceived.length - 1] ?? null;

  return {
    billCount: rows.length,
    paidBillCount: rows.filter((row) => row.status === "paid").length,
    firstBillOn: first?.receivedOn ?? null,
    lastBillNumber: last?.vendorDocNumber ?? null,
    lastBillOn: last?.receivedOn ?? null,
    lastBillTotalPaise: last?.totalPaise ?? null,
  };
}

function defaultVendorCategory(vendor: VendorOption): string {
  const name = vendor.name.toLowerCase();
  if (name.includes("industrial") || name.includes("fastener")) return "Raw material - fasteners";
  if (name.includes("metal") || name.includes("alloy")) return "Raw material - general";
  if (vendor.tdsSection === "194J" || vendor.tdsSection === "194C") return "Services";
  return "Raw material - general";
}

function sourceToMode(source: "photo" | "email_pdf" | "manual"): BillSourceMode {
  if (source === "photo") return "upload";
  if (source === "email_pdf") return "email";
  return "manual";
}

function defaultTdsRate(value: string): string {
  if (value === "194Q") return "0.1";
  if (value === "194C") return "1";
  if (value === "194J") return "10";
  return "0";
}

/**
 * Reconstitute a single bill line from persisted totals (paise strings).
 * Bills don't store line items in V1.0.x; this lets edit mode satisfy the
 * form-schema's `lineItems.min(1)` rule and reproduce the captured math.
 *
 *   qty=1, rate=subtotal, taxRate = round(tax / subtotal × 100, 2)
 *
 * The operator can split / replace the line if they want; on save the
 * recomputed totals are what get sent.
 */
function synthesiseLineFromTotals(subtotalPaise: string, taxPaise: string): BillLineItem {
  const sub = BigInt(subtotalPaise);
  const tax = BigInt(taxPaise);
  let taxRate = "0";
  if (sub > 0n) {
    // bps = tax / subtotal × 10_000  → percent two decimals = bps / 100
    const bps = Number((tax * 10_000n) / sub);
    const asPercent = (bps / 100).toFixed(2);
    // Trim trailing ".00" / ".X0" to keep the input field clean.
    taxRate = asPercent.replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
  }
  return {
    id: "line-1",
    description: "Captured bill total (line-level retention V1.x)",
    hsn: "",
    qty: "1",
    unit: "lot",
    rate: formatPaiseAsRupeeInput(sub),
    discountRate: "0",
    taxRate,
  };
}

function lineFromPersisted(line: {
  id: string;
  description: string;
  hsn: string;
  qtyMilli: number;
  unit: string;
  ratePaise: string;
  discountBps: number;
  taxRateBps: number;
}): BillLineItem {
  return {
    id: line.id,
    description: line.description,
    hsn: line.hsn,
    qty: formatMilliAsDecimal(line.qtyMilli),
    unit: line.unit,
    rate: formatPaiseAsRupeeInput(BigInt(line.ratePaise)),
    discountRate: formatBpsAsPercent(line.discountBps),
    taxRate: formatBpsAsPercent(line.taxRateBps),
  };
}

function toPersistedLineInput(lines: BillLineItem[]) {
  return lines.filter(hasValidBillLine).map((line) => ({
    description: line.description,
    hsn: line.hsn,
    qty: line.qty,
    unit: line.unit,
    rate: line.rate,
    discountRate: line.discountRate || "0",
    taxRate: line.taxRate || "0",
  }));
}

/** Paise (bigint) → rupee-decimal string ("12345" → "123.45"). */
function formatPaiseAsRupeeInput(paise: bigint): string {
  if (paise === 0n) return "0";
  const rupees = paise / 100n;
  const remainder = paise % 100n;
  if (remainder === 0n) return rupees.toString();
  return `${rupees}.${String(remainder).padStart(2, "0")}`;
}

function formatMilliAsDecimal(milli: number): string {
  const whole = Math.trunc(milli / 1000);
  const remainder = milli % 1000;
  if (remainder === 0) return String(whole);
  return `${whole}.${String(remainder).padStart(3, "0").replace(/0+$/, "")}`;
}

function formatBpsAsPercent(bps: number): string {
  const whole = Math.trunc(bps / 100);
  const remainder = bps % 100;
  if (remainder === 0) return String(whole);
  return `${whole}.${String(remainder).padStart(2, "0").replace(/0+$/, "")}`;
}

// ── Source-mode bodies ─────────────────────────────────────────────

/**
 * Renders the body content below the radio-card source picker.
 *
 *   - upload    → NEW mode: scratch upload + "Extract with AI" button.
 *                 EDIT mode: legacy staged-upload flow (file flushes via
 *                 the attachments router after the existing bill row).
 *   - email     → "Lands in V1.x" placeholder for the per-tenant inbox.
 *   - manual    → nothing — the manual entry form is the rest of the
 *                 composer body below.
 */
function SourceModeBody({
  mode,
  isEditMode,
  scratchUpload,
  scratchUploadState,
  scratchUploadError,
  extractState,
  extractError,
  extractResult,
  onScratchPick,
  onScratchClear,
  onExtract,
  pendingUpload,
  pendingUploadState,
  pendingUploadError,
  onFilePicked,
  onClearFile,
}: {
  mode: BillSourceMode;
  isEditMode: boolean;
  scratchUpload: ScratchUpload | null;
  scratchUploadState: "idle" | "uploading" | "uploaded" | "error";
  scratchUploadError: string | null;
  extractState: "idle" | "extracting" | "done" | "error";
  extractError: string | null;
  extractResult: ExtractResultSnapshot | null;
  onScratchPick: (file: File) => void;
  onScratchClear: () => void;
  onExtract: () => void;
  pendingUpload: File | null;
  pendingUploadState: "idle" | "requesting" | "uploading" | "confirming" | "done" | "error";
  pendingUploadError: string | null;
  onFilePicked: (file: File) => void;
  onClearFile: () => void;
}) {
  if (mode === "upload") {
    if (isEditMode) {
      return (
        <BillUploadDropzone
          file={pendingUpload}
          state={pendingUploadState}
          error={pendingUploadError}
          onFilePicked={onFilePicked}
          onClear={onClearFile}
        />
      );
    }
    return (
      <ScratchExtractDropzone
        scratchUpload={scratchUpload}
        scratchUploadState={scratchUploadState}
        scratchUploadError={scratchUploadError}
        extractState={extractState}
        extractError={extractError}
        extractResult={extractResult}
        onPick={onScratchPick}
        onClear={onScratchClear}
        onExtract={onExtract}
      />
    );
  }
  if (mode === "email") {
    return (
      <ComingSoonPane
        icon={Mail}
        title="Email forwarding inbox"
        body="Forward vendor bill emails to a per-tenant address and they show up here as drafts. Lands in V1.x — Settings will surface your inbox address."
      />
    );
  }
  // manual — the real form lives below the SourceCard.
  return null;
}

/**
 * ScratchExtractDropzone — new-bill upload + AI extract flow.
 *
 * The file PUTs to a tenant-scoped scratch S3 key as soon as the operator
 * picks it. After upload, the dropzone collapses into a file chip and
 * surfaces an "Extract with AI" primary button. On click the model reads
 * the PDF/photo and the parent's `applyExtractToForm` populates the
 * composer fields. On save, `bills.create` promotes the scratch object
 * into a permanent `bill_attachments` row in the same transaction.
 *
 * Edit-mode bills use the legacy `BillUploadDropzone` below — its
 * staged-flush flow needs an existing `billId` to bind to, which edit
 * mode always has and new mode never does.
 */
function ScratchExtractDropzone({
  scratchUpload,
  scratchUploadState,
  scratchUploadError,
  extractState,
  extractError,
  extractResult,
  onPick,
  onClear,
  onExtract,
}: {
  scratchUpload: ScratchUpload | null;
  scratchUploadState: "idle" | "uploading" | "uploaded" | "error";
  scratchUploadError: string | null;
  extractState: "idle" | "extracting" | "done" | "error";
  extractError: string | null;
  extractResult: ExtractResultSnapshot | null;
  onPick: (file: File) => void;
  onClear: () => void;
  onExtract: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  // Kept in sync with `ALLOWED_ATTACHMENT_TYPES` / `ATTACHMENT_MAX_BYTES`.
  const ACCEPT = "application/pdf,image/jpeg,image/png,image/webp";
  const ACCEPT_TYPES = new Set(["application/pdf", "image/jpeg", "image/png", "image/webp"]);
  const MAX_BYTES = 25 * 1024 * 1024;

  const validate = (picked: File): string | null => {
    if (!ACCEPT_TYPES.has(picked.type)) {
      return `"${picked.name}" has unsupported type "${picked.type || "unknown"}". Use PDF, JPEG, PNG, or WebP.`;
    }
    if (picked.size === 0) return `"${picked.name}" is empty.`;
    if (picked.size > MAX_BYTES) {
      const mb = (picked.size / (1024 * 1024)).toFixed(1);
      return `"${picked.name}" is ${mb} MB; max 25 MB.`;
    }
    return null;
  };

  const handlePick = (picked: File) => {
    const validationError = validate(picked);
    if (validationError) {
      toast.error("Couldn't add that file", { description: validationError });
      return;
    }
    onPick(picked);
  };

  // Empty state — the dropzone.
  if (!scratchUpload) {
    return (
      <div className="grid gap-3">
        <div
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const dropped = e.dataTransfer.files?.[0];
            if (dropped) handlePick(dropped);
          }}
          className={[
            "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-8 text-center transition-colors",
            dragging
              ? "border-sky-500 bg-sky-50"
              : "border-line-2 bg-bg hover:border-sky-300 hover:bg-sky-50/50",
          ].join(" ")}
        >
          <span className="grid size-11 place-items-center rounded-full bg-paper text-sky-700 shadow-sm">
            <Upload size={20} strokeWidth={2} aria-hidden />
          </span>
          <p className="text-sm font-semibold text-ink">
            Drop the bill PDF or photo here, or{" "}
            <span className="text-sky-700 underline">browse</span>
          </p>
          <p className="text-xs text-muted">PDF, JPEG, PNG, or WebP · max 25 MB</p>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="sr-only"
            onChange={(e) => {
              const picked = e.target.files?.[0];
              if (picked) handlePick(picked);
              e.target.value = "";
            }}
          />
        </div>
        <div className="flex items-start gap-2 rounded-md border border-line bg-raised px-3 py-2 text-xs text-muted">
          <Sparkles className="mt-0.5 size-3.5 shrink-0 text-sky-700" aria-hidden />
          <span>
            <span className="font-semibold text-ink-2">AI auto-fill</span> — after upload, click
            Extract to read the vendor, totals, and line items off the file. Vision model on
            OpenRouter; usually 5–15 seconds.
          </span>
        </div>
      </div>
    );
  }

  // Uploaded / uploading / extracting / extracted — file chip + actions.
  const isUploading = scratchUploadState === "uploading";
  const isUploadError = scratchUploadState === "error";
  const isUploaded = scratchUploadState === "uploaded";
  const isExtracting = extractState === "extracting";
  const hasExtracted = extractState === "done" && extractResult !== null;
  const isExtractError = extractState === "error";
  const sizeMb = (scratchUpload.file.size / (1024 * 1024)).toFixed(2);

  const statusLabel = isUploadError
    ? (scratchUploadError ?? "Upload failed.")
    : isUploading
      ? "Uploading to secure storage…"
      : isExtracting
        ? "Reading bill (5–15s)…"
        : hasExtracted
          ? `Extracted at ${Math.round((extractResult?.overallConfidence ?? 0) * 100)}% confidence · review every field before saving`
          : isUploaded
            ? "Ready — hit Extract to fill the form, or fill it manually below."
            : "";

  const tone = isUploadError || isExtractError ? "bad" : hasExtracted ? "emerald" : "sky";

  return (
    <div className="grid gap-3">
      <div
        className={[
          "flex items-start gap-3 rounded-md border p-3",
          tone === "bad"
            ? "border-bad-200 bg-bad-50"
            : tone === "emerald"
              ? "border-emerald-200 bg-emerald-50/40"
              : "border-sky-200 bg-sky-50/40",
        ].join(" ")}
      >
        <span
          className={[
            "grid size-9 shrink-0 place-items-center rounded-md",
            tone === "bad"
              ? "bg-bad-100 text-bad"
              : tone === "emerald"
                ? "bg-emerald-100 text-emerald-700"
                : "bg-sky-100 text-sky-700",
          ].join(" ")}
        >
          {isUploading || isExtracting ? (
            <Loader2 size={16} className="animate-spin" />
          ) : isUploadError || isExtractError ? (
            <X size={16} />
          ) : hasExtracted ? (
            <Check size={16} />
          ) : (
            <FileText size={16} />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-ink" title={scratchUpload.file.name}>
            {scratchUpload.file.name}
          </p>
          <p className={["mt-0.5 text-xs", tone === "bad" ? "text-bad" : "text-muted"].join(" ")}>
            {sizeMb} MB{statusLabel ? ` · ${statusLabel}` : ""}
          </p>
        </div>
        {!isUploading && !isExtracting ? (
          <button
            type="button"
            onClick={onClear}
            className="grid size-7 shrink-0 place-items-center rounded-sm text-muted hover:bg-paper hover:text-ink"
            aria-label="Remove file"
          >
            <X size={14} />
          </button>
        ) : null}
      </div>

      {/* Extract CTA / extracted state. Hidden while uploading; surfaces a
       *  primary button once the scratch object is in place. */}
      {isUploaded || isExtracting || hasExtracted || isExtractError ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line bg-paper px-3 py-2.5">
          <div className="flex items-start gap-2 text-xs text-muted">
            <Sparkles className="mt-0.5 size-3.5 shrink-0 text-sky-700" aria-hidden />
            <span>
              {hasExtracted ? (
                <>
                  <span className="font-semibold text-ink-2">Extracted from PDF</span> — totals are
                  recomputed from the line items below, not the model's reported numbers.
                </>
              ) : isExtractError ? (
                <>
                  <span className="font-semibold text-bad">Extraction failed</span> —{" "}
                  {extractError ?? "try again or fill in manually."}
                </>
              ) : (
                <>
                  <span className="font-semibold text-ink-2">AI auto-fill</span> — click Extract to
                  read vendor, totals, and line items off the file. ~8–15 seconds.
                </>
              )}
            </span>
          </div>
          <Button
            type="button"
            intent={hasExtracted ? "ghost" : "primary"}
            size="sm"
            onClick={onExtract}
            disabled={isExtracting}
          >
            {isExtracting ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Reading…
              </>
            ) : hasExtracted ? (
              <>
                <Sparkles size={14} />
                Re-extract
              </>
            ) : (
              <>
                <Sparkles size={14} />
                Extract with AI
              </>
            )}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * BillUploadDropzone — legacy dropzone for the "Upload PDF/photo" mode
 * in EDIT mode only. New-bill mode uses `ScratchExtractDropzone` above.
 *
 * The file is **staged** in parent state until the bill row exists.
 * Once `bills.create` returns the new id, an effect in the parent flushes
 * the file through `bills.attachments.requestUpload → S3 PUT → confirm`.
 */
function BillUploadDropzone({
  file,
  state,
  error,
  onFilePicked,
  onClear,
}: {
  file: File | null;
  state: "idle" | "requesting" | "uploading" | "confirming" | "done" | "error";
  error: string | null;
  onFilePicked: (file: File) => void;
  onClear: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  // Mirrors bill-attachments-card.tsx (kept in sync — the server re-validates).
  const ACCEPT = "application/pdf,image/jpeg,image/png,image/webp";
  const ACCEPT_TYPES = new Set(["application/pdf", "image/jpeg", "image/png", "image/webp"]);
  const MAX_BYTES = 25 * 1024 * 1024;

  const stateLabel: Record<typeof state, string> = {
    idle: "Ready to attach when you save the draft.",
    requesting: "Requesting upload slot…",
    uploading: "Uploading to secure storage…",
    confirming: "Confirming on the bill…",
    done: "Attached. See it in the Attachments section below.",
    error: error ?? "Upload failed.",
  };

  const validate = (picked: File): string | null => {
    if (!ACCEPT_TYPES.has(picked.type)) {
      return `"${picked.name}" has unsupported type "${picked.type || "unknown"}". Use PDF, JPEG, PNG, or WebP.`;
    }
    if (picked.size === 0) return `"${picked.name}" is empty.`;
    if (picked.size > MAX_BYTES) {
      const mb = (picked.size / (1024 * 1024)).toFixed(1);
      return `"${picked.name}" is ${mb} MB; max 25 MB.`;
    }
    return null;
  };

  const handlePick = (picked: File) => {
    const validationError = validate(picked);
    if (validationError) {
      toast.error("Couldn't add that file", { description: validationError });
      return;
    }
    onFilePicked(picked);
  };

  // Empty state — show the big dropzone.
  if (!file) {
    return (
      <div className="grid gap-3">
        <div
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const dropped = e.dataTransfer.files?.[0];
            if (dropped) handlePick(dropped);
          }}
          className={[
            "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-8 text-center transition-colors",
            dragging
              ? "border-sky-500 bg-sky-50"
              : "border-line-2 bg-bg hover:border-sky-300 hover:bg-sky-50/50",
          ].join(" ")}
        >
          <span className="grid size-11 place-items-center rounded-full bg-paper text-sky-700 shadow-sm">
            <Upload size={20} strokeWidth={2} aria-hidden />
          </span>
          <p className="text-sm font-semibold text-ink">
            Drop the bill PDF or photo here, or{" "}
            <span className="text-sky-700 underline">browse</span>
          </p>
          <p className="text-xs text-muted">PDF, JPEG, PNG, or WebP · max 25 MB</p>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="sr-only"
            onChange={(e) => {
              const picked = e.target.files?.[0];
              if (picked) handlePick(picked);
              e.target.value = "";
            }}
          />
        </div>
      </div>
    );
  }

  // Staged / uploading / done — show file chip with state.
  const isError = state === "error";
  const isWorking = state === "requesting" || state === "uploading" || state === "confirming";
  const isDone = state === "done";
  const sizeMb = (file.size / (1024 * 1024)).toFixed(2);

  return (
    <div
      className={[
        "flex items-start gap-3 rounded-md border p-3",
        isError
          ? "border-bad-200 bg-bad-50"
          : isDone
            ? "border-emerald-200 bg-emerald-50/40"
            : "border-sky-200 bg-sky-50/40",
      ].join(" ")}
    >
      <span
        className={[
          "grid size-9 shrink-0 place-items-center rounded-md",
          isError
            ? "bg-bad-100 text-bad"
            : isDone
              ? "bg-emerald-100 text-emerald-700"
              : "bg-sky-100 text-sky-700",
        ].join(" ")}
      >
        {isWorking ? (
          <Loader2 size={16} className="animate-spin" />
        ) : isError ? (
          <X size={16} />
        ) : (
          <FileText size={16} />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-ink" title={file.name}>
          {file.name}
        </p>
        <p className={["mt-0.5 text-xs", isError ? "text-bad" : "text-muted"].join(" ")}>
          {sizeMb} MB · {stateLabel[state]}
        </p>
      </div>
      {(state === "idle" || state === "error") && (
        <button
          type="button"
          onClick={onClear}
          className="grid size-7 shrink-0 place-items-center rounded-sm text-muted hover:bg-paper hover:text-ink"
          aria-label="Remove file"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}

function ComingSoonPane({
  icon: Icon,
  title,
  body,
}: {
  icon: typeof FileText;
  title: string;
  body: string;
}) {
  return (
    <div className="flex items-start gap-3 rounded-md border border-dashed border-line bg-bg p-4">
      <span
        aria-hidden
        className="grid size-9 shrink-0 place-items-center rounded-md bg-paper text-muted"
      >
        <Icon size={16} strokeWidth={2} />
      </span>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-ink">{title}</p>
        <p className="mt-1 text-xs text-muted leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

// ── Extract → form mapping ──────────────────────────────────────────

/**
 * Structural shape we need to match a vendor — narrow enough that the
 * tRPC `vendors.list` row + the local `VendorOption` both satisfy it.
 * Keeping it inline avoids dragging the full vendor row type up the
 * tree.
 */
interface VendorMatchCandidate {
  id: string;
  name: string;
  gstin: string | null;
  state: string | null;
}

/**
 * Apply a `bills.extract` response into the form via `form.reset(...)`.
 * Mutates the form and returns the extracted vendor name when no match
 * was found (`null` on success). The unmatched name is surfaced as a
 * hint chip above the vendor picker so the operator knows what the
 * model read and can either pick a matching vendor or create one.
 *
 * Vendor matching is strict: GSTIN exact (case-insensitive) > normalized
 * name exact. Fuzzy matching lands as a follow-up — duplicate vendor
 * rows from sloppy auto-matches are worse than a 30-second pick.
 */
function applyExtractToForm(
  snapshot: ExtractResultSnapshot,
  vendors: ReadonlyArray<VendorMatchCandidate>,
  form: UseFormReturn<BillFormValues>,
): string | null {
  const extractedVendor = snapshot.fields.vendor;
  const matched = matchVendorFromExtract(extractedVendor, vendors);

  const lines = snapshot.fields.lineItems;
  const lineItems: BillLineItem[] =
    lines.length > 0
      ? lines.map((line, idx) => ({
          id: `extract-line-${idx + 1}`,
          description: line.description,
          hsn: line.hsn,
          qty: line.qty,
          unit: line.unit,
          rate: line.rate,
          discountRate: line.discountRate,
          taxRate: line.taxRate,
        }))
      : [{ ...EMPTY_LINE }];

  // Preserve the slots the model can't see (tax mode, payment terms,
  // currency, category, approver, vendorNote). The extracted notes go
  // into `internalNote` per the extractor's own convention.
  const current = form.getValues();
  form.reset({
    ...current,
    sourceMode: "upload",
    extractionState: "extracted",
    vendorId: matched?.id ?? "",
    vendorDocNumber: snapshot.fields.vendorDocNumber,
    receivedOn: snapshot.fields.receivedOn,
    dueOn: snapshot.fields.dueOn ?? "",
    placeOfSupply: matched?.state ?? current.placeOfSupply ?? "",
    lineItems,
    internalNote: snapshot.fields.notes ?? current.internalNote ?? "",
  });

  return matched ? null : extractedVendor.name;
}

/**
 * Build the `aiExtraction` payload for `bills.create` from the stashed
 * extract snapshot. Null when the operator never ran extract.
 *
 * The contract calls it `modelVersion` but the extractor returns
 * `promptVersion` — the prompt revision is what changes the model's
 * behaviour for a fixed model id, so it's the value we want stamped on
 * the audit row.
 */
function buildAiExtractionPayload(snapshot: ExtractResultSnapshot | null) {
  if (!snapshot) return null;
  return {
    model: snapshot.model,
    modelVersion: snapshot.promptVersion,
    fieldConfidence: snapshot.fieldConfidence,
  };
}

/**
 * Stringify the aggregate confidence for the wire. The contract takes a
 * decimal string with 1-3 fractional digits — `.toFixed(3)` always
 * matches that regex.
 */
function buildOverallConfidencePayload(snapshot: ExtractResultSnapshot | null) {
  if (!snapshot) return null;
  return snapshot.overallConfidence.toFixed(3);
}

/**
 * Build the partial `bills.create` input for promoting the scratch
 * upload to a permanent attachment. Empty object when there's no
 * uploaded scratch file (operator typed manually or only partially
 * uploaded). The server expects the two fields to travel together.
 */
function buildScratchPromotePayload(
  scratchUpload: ScratchUpload | null,
  scratchUploadState: "idle" | "uploading" | "uploaded" | "error",
) {
  if (!scratchUpload || scratchUploadState !== "uploaded") return {};
  return {
    extractionSourceObjectKey: scratchUpload.objectKey,
    extractionAttachmentMeta: {
      originalName: scratchUpload.file.name,
      contentType: scratchUpload.contentType,
      sizeBytes: scratchUpload.sizeBytes,
    },
  };
}

function matchVendorFromExtract(
  extracted: { name: string; gstin: string | null },
  vendors: ReadonlyArray<VendorMatchCandidate>,
): VendorMatchCandidate | null {
  if (extracted.gstin) {
    const target = extracted.gstin.toUpperCase();
    const byGstin = vendors.find(
      (v) => typeof v.gstin === "string" && v.gstin.toUpperCase() === target,
    );
    if (byGstin) return byGstin;
  }
  const normalized = extracted.name.trim().toLowerCase();
  if (normalized) {
    const byName = vendors.find((v) => v.name.trim().toLowerCase() === normalized);
    if (byName) return byName;
  }
  return null;
}
